// ModalBox.tsx
import { useConfig } from '../ConfigProvider';
import { evaluateTemplate } from './utils/Utils';
import { useTheme } from '@mui/material/styles';
import { AppBar, Box, Button, Modal, SxProps, Theme, Toolbar, Typography } from '@mui/material';
import ControlRenderer from './ControlRenderer';
import ButtonControl, { ButtonControl as ButtonControlInterface } from './controls/ButtonControl';
import { FormModeType, useFormData } from '../FormDataProvider';

/**
 * Defines an action with a type, optional payload, and potential success and failure actions.
 */
export interface Action {
  /**
   * The type of action being performed. Possible types include:
   * - `'clearformdata'`: Clears form data for a specified data key.
   * - `'setfieldvalue'`: Sets form data filed value for a specified data key.
   * - `'fetchformdata'`: Fetches form data from a specified source.
   * - `'loadform'`: Loads form data and navigates to an edit page.
   * - `'postdata' / 'postdata2'`: Sends data to a specified endpoint. The `'postdata2'` variant handles JSON responses and updates the state.
   * - `'navigate'`: Opens a URL in a new tab based on a specified endpoint.
   * - `'pagenav'`: Navigates to a different page within the app.
   * - `'messagebox'`: Displays a message box.
   * - (Add additional custom action types as needed.)
   */
  type: string;

  /**
   * An optional data payload associated with the action.
   * This can include keys for individual data points, endpoints, or additional contextual information.
   */
  payload?: any;

  /**
   * An action to be executed on successful completion of this action.
   * Enables chaining to handle successive operations in workflows.
   */
  successAction?: Action;

  /**
   * An action to be executed if this action fails.
   * Useful for error handling and remedial processes within an action's lifecycle.
   */
  failureAction?: Action;

  /**
   * An action to be executed if this action completes (without success or failure).
   * Useful for error handling and remedial processes within an action's lifecycle.
   */
  completeAction?: Action;
}

interface ModalBoxProps {
  action: Action;
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
  onComplete: () => void;
  onError: () => void;
  onClose: () => void;
  sx?: SxProps<Theme>;
}

const ModalBox: React.FC<ModalBoxProps> = ({ action, data, onDataChange, mode, onComplete, onError, onClose, sx }) => {
  const theme = useTheme();
  const { appName } = useConfig();
  const { formData } = useFormData();

  const handleClose = (event: any) => {
    event.stopPropagation();
    onClose();
  };

  const handleOK = (event: any) => {
    event.stopPropagation();
    onComplete();
  };

  const handleCancel = (event: any) => {
    event.stopPropagation();
    onError();
  };

  return (
    <>
      <Modal
        open={true}
        disableScrollLock
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          className="eform-action-modal"
          sx={{
            ...sx,
            backgroundColor: theme.palette.background.default, // Set to the system's background color
          }}
        >
          {!action.payload?.hideHeader && (
            <AppBar position="static" color="primary" className="eform-action-modal-head" sx={{ paddingLeft: 1 }}>
              <Toolbar className="eform-action-modal-toolbar">
                <Typography variant="h6" component="div" className="eform-action-modal-title">
                  {action.payload?.title ? evaluateTemplate(action.payload.title, data, formData) : appName}
                </Typography>
              </Toolbar>
            </AppBar>
          )}
          <Box
            sx={{
              width: '100%',
              height: '4px',
              backgroundColor: 'orange',
            }}
          />
          <Box sx={{ 
            paddingX: 3 ,
            paddingY: 2 ,
            maxHeight: '80vh', // Limit the height of the modal
            overflowY: 'auto', // Enable vertical scrolling if content exceeds max height
            }}>
            {action.payload && action.payload.layout && action.payload.layout.type &&
              <ControlRenderer control={action.payload.layout} data={data} onDataChange={onDataChange} mode={action.payload.mode ?? mode} />}
            {action.payload && action.payload.message &&
              <Typography variant="body1" id="modal-description" gutterBottom>
                {evaluateTemplate(action.payload.message, data, formData)}
              </Typography>}
          </Box>
          {action.successAction && action.successAction.type && action.payload.title &&
            <ButtonControl
              control={{
                type: "button",
                label: action.payload.title,
                clickAction: action.successAction,
                disableIfInvalid: action.payload.disableIfInvalid
              } as ButtonControlInterface}
              data={data}
              mode="edit"
              onClose={onClose}
              onComplete={onClose} // in both cases the modal should just be closed
              onError={onError}
            />}
          {action.payload && action.payload.okbutton &&
            <Button className="eform-action-modal-button" variant="contained" onClick={handleOK} color="primary">
              OK
            </Button>}
          {action.payload && action.payload.cancelbutton &&
            <Button className="eform-action-modal-button" variant="contained" onClick={handleCancel} color="primary">
              Cancel
            </Button>}
          {action.payload && action.payload.closebutton &&
            <Button className="eform-action-modal-button" variant="contained" onClick={handleClose} color="primary">
              {action.payload.closebuttonLabel || 'Close'}
            </Button>}
        </Box>
      </Modal>
    </>
  );
};

export default ModalBox;
